import { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { QDropdownWrapper, QButton, QTypography } from '@kw/quokka-ui';

import { OrganizationDropdown } from './OrganizationDropdown';
import { useFetchService } from '../../utils/fetchService';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { sendHttpRequest } from '../../utils/network.service';

const DEFAULT_POLICIES = [
  {
    policyName: 'Malicious Apps',
    policyDescription:
      "Apps that trigger Quokka's Malware Detection engine, or have a contained file that matches a known malware signature.",
    ruleName: 'Malware',
  },
  {
    policyName: 'Overprivileged Apps',
    policyDescription: 'Apps that ask for more permissions than they programmatically use.',
    ruleName: 'Requests more permissions than it uses',
  },
  {
    policyName: 'Leaky Apps',
    policyDescription: 'Apps that leak or expose personally identifiable information (PII) or other important user data.',
    ruleName: 'Leaks critical user data, password or other information',
  },
  {
    policyName: 'Chatty Apps',
    policyDescription:
      'Apps that interact with the messaging or phone functionality on devices, giving them the ability to communicate externally.',
    ruleName: 'App with communication capabilities',
  },
  {
    policyName: 'Zombie Apps',
    policyDescription: 'Apps that remain persistent in memory after they have been closed, causing issues like high battery drain.',
    ruleName: 'Memory persistence',
  },
  {
    policyName: 'Harvester Apps',
    policyDescription:
      'Apps potentially providing a popular or important function which harvest user data by accessing sensitive permissions and sharing that data with Ad/Social Networks and/or risky locations.',
    ruleName: 'App requests permissions and shares data',
  },
  {
    policyName: 'Sloppy Apps',
    policyDescription:
      'Apps that do not use industry standard data protection mechanisms, violating OWASP M7 mobile risks for client code quality.',
    ruleName: 'App with poor code standards',
  },
];

export const Policies = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedPolicy, setSelectedPolicy] = useState<string>('');
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [accessToken = ''] = useLocalStorage('accessToken', '');

  const { data: rulesResp = { results: [] } } = useFetchService(`/eval/rules/list`, accessToken);

  const rulesByName = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const r of rulesResp.results ?? []) map.set(r.name, r);
    return map;
  }, [rulesResp.results]);

  const policyOptions = useMemo(
    () =>
      DEFAULT_POLICIES.map(p => ({
        label: `${p.policyName} — ${p.ruleName}`,
        value: p.policyName,
      })),
    [],
  );

  const canCreateOne = Boolean(selectedOrganization && selectedPolicy);
  const canCreateAll = Boolean(selectedOrganization);

  const createPolicy = useCallback(
    async ({ policyName, policyDescription, ruleId }: { policyName: string; policyDescription: string; ruleId: string }) => {
      if (!selectedOrganization) {
        enqueueSnackbar('Please select an organization first.', { variant: 'warning' });
        return;
      }
      if (!ruleId) {
        enqueueSnackbar(`No matching rule found for "${policyName}".`, { variant: 'error' });
        return;
      }

      await sendHttpRequest({
        path: `${process.env.EVAL_SERVICE_URL}/eval/policies/create/global`,
        method: 'POST',
        body: {
          orgId: selectedOrganization,
          name: policyName,
          description: policyDescription,
          enabled: false,
          policyRules: [
            {
              ruleId,
            },
          ],
        },
        successMessage: `Policy "${policyName}" created successfully`,
        errorMessage: `Error creating policy "${policyName}"`,
        enqueueSnackbar,
      });
    },
    [enqueueSnackbar, selectedOrganization],
  );

  const createSinglePolicy = useCallback(
    async (policyName: string) => {
      const matched = DEFAULT_POLICIES.find(p => p.policyName === policyName);
      if (!matched) {
        enqueueSnackbar(`Unknown policy "${policyName}".`, { variant: 'error' });
        return;
      }
      const rule = rulesByName.get(matched.ruleName);
      await createPolicy({
        policyName: matched.policyName,
        policyDescription: matched.policyDescription,
        ruleId: rule?.id ?? '',
      });
    },
    [createPolicy, enqueueSnackbar, rulesByName],
  );

  const createAllPolicies = useCallback(async () => {
    const tasks = DEFAULT_POLICIES.map(p => {
      const rule = rulesByName.get(p.ruleName);
      return createPolicy({
        policyName: p.policyName,
        policyDescription: p.policyDescription,
        ruleId: rule?.id ?? '',
      });
    });

    const results = await Promise.allSettled(tasks);
    const ok = results.filter(r => r.status === 'fulfilled').length;
    const fail = results.length - ok;

    enqueueSnackbar(`Created ${ok} policies${fail ? `, ${fail} failed` : ''}.`, {
      variant: fail ? 'warning' : 'success',
    });
  }, [createPolicy, enqueueSnackbar, rulesByName]);

  return (
    <>
      <OrganizationDropdown setSelectedOrganization={setSelectedOrganization} />

      <Box p={2}>
        <QTypography variant="h4Bold">Policy</QTypography>

        <QDropdownWrapper
          selectedOption={selectedPolicy}
          closedLabel={policyOptions.find(o => o.value === selectedPolicy)?.label ?? 'Select Policy'}
          formControlProps={{ sx: { width: '100%' } }}
          onChange={e => setSelectedPolicy(e.target.value as string)}
          options={policyOptions}
        />
      </Box>

      <Box p={2}>
        <QButton disabled={!canCreateOne} variant="filled" onClick={() => createSinglePolicy(selectedPolicy)}>
          Create Policy
        </QButton>

        <QButton sx={{ ml: 1 }} variant="outlined" disabled={!canCreateAll} onClick={createAllPolicies}>
          Create All Policies
        </QButton>
      </Box>
    </>
  );
};
